import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import { CreateMenu, Menu } from '../types/menu'
import { loadMenus, modifyMenu, removeMenu, saveMenu } from '../api/menu'

export const getMenus = createAsyncThunk(
  'menu/getMenus',
  async (_, thunkApi) => {
    try {
      return await loadMenus()
    } catch (e) {
      return thunkApi.rejectWithValue('getMenusError')
    }
  },
)

export const addMenu = createAsyncThunk(
  'menu/addMenu',
  async (menu: CreateMenu, thunkApi) => {
    try {
      return await saveMenu(menu)
    } catch (e) {
      return thunkApi.rejectWithValue('addMenuError')
    }
  },
)

export const updateMenu = createAsyncThunk(
  'menu/updateMenu',
  async (menu: Menu, thunkApi) => {
    try {
      return await modifyMenu(menu)
    } catch (e) {
      return thunkApi.rejectWithValue('updateMenuError')
    }
  },
)

export const deleteMenu = createAsyncThunk(
  'menu/deleteMenu',
  async (id: string, thunkApi) => {
    try {
      return await removeMenu(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteMenuError')
    }
  },
)

interface MenuSliceState {
  menus: Menu[]
  loading: boolean
  error?: SerializedError
}

const initialState: MenuSliceState = {
  menus: [],
  loading: false,
  error: undefined,
}

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getMenus.fulfilled, (state, action: PayloadAction<Menu[]>) => {
      state.menus = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getMenus.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getMenus.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addMenu.fulfilled, (state, action: PayloadAction<Menu>) => {
      state.menus = [action.payload, ...state.menus]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addMenu.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addMenu.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateMenu.fulfilled, (state, action: PayloadAction<Menu>) => {
      state.menus = state.menus.map((menu) =>
        menu.id === action.payload.id ? action.payload : menu,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateMenu.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateMenu.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      deleteMenu.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.menus = state.menus.filter((menu) => menu.id !== action.payload)
        state.loading = false
        state.error = undefined
      },
    )
    builder.addCase(deleteMenu.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteMenu.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export default menuSlice.reducer
